import React, { useState, useEffect } from "react";
import { Building2, Save, CheckCircle, RefreshCw, AlertTriangle, Upload, Clock, XCircle } from "lucide-react";
import { authAPI, documentsAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const INDUSTRIES = [
  "Oil & Gas", "Construction", "Healthcare", "Information Technology",
  "Agriculture", "Energy", "Security Services", "Legal Services",
  "Catering & Hospitality", "Telecommunications", "Engineering",
  "Environment", "Water & Sanitation", "Humanitarian", "Manufacturing",
  "Logistics & Supply Chain", "Finance & Banking", "Consulting", "Other",
];

const NIGERIAN_STATES = [
  "Federal", "Lagos", "Rivers", "Abuja (FCT)", "Kano", "Ogun",
  "Delta", "Bayelsa", "Cross River", "Akwa Ibom", "Edo", "Imo",
  "Enugu", "Anambra", "Kaduna", "Oyo", "Osun", "Kwara",
  "Borno", "Niger", "Plateau", "Gombe", "Sokoto", "Other",
];

const CATEGORIES = [
  "Works", "Goods", "Services", "Consultancy", "ICT", "Healthcare",
  "Education", "Oil & Gas", "Agriculture", "Security", "Environment", "Marine",
];

const inputStyle = {
  width: "100%", padding: "10px 14px",
  background: "var(--bg-secondary)", border: "1px solid var(--border)",
  borderRadius: "8px", color: "var(--text-primary)", fontSize: "13px", outline: "none",
};

export default function Companies() {
  const { user } = useAuth();

  const [form, setForm] = useState({
    name: "", registration_number: "", industry: "",
    sub_industry: "", state: "", address: "",
    phone: "", email: "", website: "", description: "",
    preferred_categories: [], preferred_states: [],
    min_contract_value: "", max_contract_value: "",
  });

  const [saving, setSaving]     = useState(false);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [saved, setSaved]       = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadingType, setUploadingType] = useState(null);

  const loadDocuments = () => {
    documentsAPI.mine()
      .then(res => setDocuments(res.data.documents || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (activeTab === "compliance") loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleUploadDocument = async (documentType, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large — 5MB limit."); return; }
    setUploadingType(documentType);
    try {
      const formData = new FormData();
      formData.append("document_type", documentType);
      formData.append("file", file);
      await documentsAPI.upload(formData);
      toast.success(`${documentType} uploaded — pending admin review`);
      loadDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingType(null);
    }
  };

  const handleDeleteDocument = async (id) => {
    try {
      await documentsAPI.remove(id);
      toast.success("Document removed");
      loadDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove document");
    }
  };

  // Load company from backend API, fall back to localStorage cache
  useEffect(() => {
    if (user?.company_id) {
      setLoading(true);
      authAPI.getCompany()
        .then(res => {
          const c = res.data;
          setForm(f => ({
            ...f,
            name:                c.name                || "",
            registration_number: c.registration_number || "",
            industry:            c.industry            || "",
            sub_industry:        c.sub_industry        || "",
            state:               c.state               || "",
            address:             c.address             || "",
            phone:               c.phone               || "",
            email:               c.email               || "",
            website:             c.website             || "",
            description:         c.description         || "",
            preferred_categories: c.preferred_categories || [],
            preferred_states:    c.preferred_states    || [],
            min_contract_value:  c.min_contract_value  || "",
            max_contract_value:  c.max_contract_value  || "",
          }));
          localStorage.setItem("company_profile", JSON.stringify(c));
        })
        .catch(() => {
          // Fall back to localStorage cache
          const cached = localStorage.getItem("company_profile");
          if (cached) {
            try {
              const c = JSON.parse(cached);
              setForm(f => ({
                ...f,
                name:                c.name                || "",
                registration_number: c.registration_number || "",
                industry:            c.industry            || "",
                sub_industry:        c.sub_industry        || "",
                state:               c.state               || "",
                address:             c.address             || "",
                phone:               c.phone               || "",
                email:               c.email               || "",
                website:             c.website             || "",
                description:         c.description         || "",
                preferred_categories: c.preferred_categories || [],
                preferred_states:    c.preferred_states    || [],
                min_contract_value:  c.min_contract_value  || "",
                max_contract_value:  c.max_contract_value  || "",
              }));
            } catch {}
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleArr = (key, val) =>
    setForm(f => ({
      ...f,
      [key]: f[key].includes(val)
        ? f[key].filter(x => x !== val)
        : [...f[key], val],
    }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      await authAPI.updateCompany({
        name:                form.name                || undefined,
        registration_number: form.registration_number || undefined,
        industry:            form.industry            || undefined,
        sub_industry:        form.sub_industry        || undefined,
        state:               form.state               || undefined,
        address:             form.address             || undefined,
        phone:               form.phone               || undefined,
        email:               form.email               || undefined,
        website:             form.website             || undefined,
        description:         form.description         || undefined,
        preferred_categories: form.preferred_categories,
        preferred_states:    form.preferred_states,
        min_contract_value:  form.min_contract_value ? Number(form.min_contract_value) : undefined,
        max_contract_value:  form.max_contract_value ? Number(form.max_contract_value) : undefined,
      });

      // Update cache
      localStorage.setItem("company_profile", JSON.stringify({
        ...form,
        min_contract_value: form.min_contract_value,
        max_contract_value: form.max_contract_value,
      }));
      // Also mark onboarding complete since they've set up their company
      localStorage.setItem("onboarding_done", "true");

      setSaved(true);
      toast.success("Company profile saved!");
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save company profile");
    }
    setSaving(false);
  };

  const tabs = [
    { id: "details",     label: "Company Details"   },
    { id: "preferences", label: "Tender Preferences" },
    { id: "compliance",  label: "Compliance Info"   },
  ];

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "40vh", color: "var(--text-muted)", gap: "12px" }}>
      <RefreshCw size={18} style={{ animation: "spin 0.8s linear infinite" }} />
      <style>{"@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}"}</style>
      Loading company profile...
    </div>
  );

  return (
    <div style={{ maxWidth: "860px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
            <div style={{ width: "36px", height: "36px", background: "var(--accent-green-dim)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Building2 size={18} color="var(--accent-green)" />
            </div>
            <h1 style={{ fontSize: "26px" }}>Company Profile</h1>
          </div>
          <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
            Your company details power AI matching, compliance tracking and bid generation.
          </p>
        </div>

        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
          style={{ padding: "10px 24px", flexShrink: 0 }}
        >
          {saving ? (
            <><RefreshCw size={14} style={{ animation: "spin 0.8s linear infinite" }} /> Saving...</>
          ) : saved ? (
            <><CheckCircle size={14} /> Saved!</>
          ) : (
            <><Save size={14} /> Save Changes</>
          )}
        </button>
      </div>

      {/* No company yet notice */}
      {!user?.company_id && (
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", padding: "14px 16px", background: "rgba(255,107,53,0.06)", border: "1px solid rgba(255,107,53,0.2)", borderRadius: "12px", marginBottom: "24px" }}>
          <AlertTriangle size={16} color="var(--accent-orange)" style={{ flexShrink: 0, marginTop: "1px" }} />
          <div style={{ fontSize: "13px", color: "var(--text-secondary)", lineHeight: "1.6" }}>
            <strong style={{ color: "var(--text-primary)" }}>Complete your profile</strong> — Fill in your company details below and click Save Changes. This unlocks AI matching, win probability and the compliance hub.
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "24px", background: "var(--bg-secondary)", padding: "4px", borderRadius: "12px", width: "fit-content" }}>
        {tabs.map(({ id, label }) => (
          <button key={id} onClick={() => setActiveTab(id)} style={{
            padding: "8px 18px", borderRadius: "10px", border: "none",
            background: activeTab === id ? "var(--bg-card)" : "transparent",
            color: activeTab === id ? "var(--accent-green)" : "var(--text-secondary)",
            cursor: "pointer", fontSize: "13px", fontWeight: "500", transition: "all 0.15s",
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── DETAILS TAB ──────────────────────────────────────────── */}
      {activeTab === "details" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div className="card">
            <h3 style={{ fontSize: "15px", fontWeight: "700", marginBottom: "16px" }}>Basic Information</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Company Name *</label>
                <input value={form.name} onChange={e => update("name", e.target.value)} placeholder="Your Company Ltd" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>CAC / RC Number</label>
                <input value={form.registration_number} onChange={e => update("registration_number", e.target.value)} placeholder="RC-123456 or BN-654321" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Industry</label>
                <select value={form.industry} onChange={e => update("industry", e.target.value)} style={inputStyle}>
                  <option value="">Select industry</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Sub-Industry</label>
                <input value={form.sub_industry} onChange={e => update("sub_industry", e.target.value)} placeholder="e.g. Upstream, Civil Works" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>State</label>
                <select value={form.state} onChange={e => update("state", e.target.value)} style={inputStyle}>
                  <option value="">Select state</option>
                  {NIGERIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Address</label>
                <input value={form.address} onChange={e => update("address", e.target.value)} placeholder="123 Main Street, Port Harcourt, Rivers State" style={inputStyle} />
              </div>
            </div>
          </div>

          <div className="card">
            <h3 style={{ fontSize: "15px", fontWeight: "700", marginBottom: "16px" }}>Contact Information</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Company Phone</label>
                <input value={form.phone} onChange={e => update("phone", e.target.value)} placeholder="+234 801 000 0000" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Company Email</label>
                <input type="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="info@yourcompany.com" style={inputStyle} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Website</label>
                <input value={form.website} onChange={e => update("website", e.target.value)} placeholder="https://yourcompany.com" style={inputStyle} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Company Description</label>
                <textarea
                  value={form.description}
                  onChange={e => update("description", e.target.value)}
                  placeholder="Brief description of your company's core business and capabilities..."
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical", lineHeight: "1.6" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── PREFERENCES TAB ──────────────────────────────────────── */}
      {activeTab === "preferences" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div className="card">
            <h3 style={{ fontSize: "15px", fontWeight: "700", marginBottom: "4px" }}>Tender Categories</h3>
            <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "16px" }}>Select all categories your company bids on — powers AI matching.</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {CATEGORIES.map(cat => {
                const sel = form.preferred_categories.includes(cat);
                return (
                  <button key={cat} onClick={() => toggleArr("preferred_categories", cat)} style={{ padding: "7px 16px", borderRadius: "100px", border: `1px solid ${sel ? "var(--accent-green)" : "var(--border)"}`, background: sel ? "var(--accent-green-dim)" : "transparent", color: sel ? "var(--accent-green)" : "var(--text-secondary)", fontSize: "12px", fontWeight: "600", cursor: "pointer", transition: "all 0.15s" }}>
                    {sel ? "✓ " : ""}{cat}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="card">
            <h3 style={{ fontSize: "15px", fontWeight: "700", marginBottom: "4px" }}>Target States</h3>
            <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "16px" }}>States where you actively look for tenders.</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {NIGERIAN_STATES.map(st => {
                const sel = form.preferred_states.includes(st);
                return (
                  <button key={st} onClick={() => toggleArr("preferred_states", st)} style={{ padding: "7px 16px", borderRadius: "100px", border: `1px solid ${sel ? "var(--accent-blue)" : "var(--border)"}`, background: sel ? "rgba(79,142,247,0.1)" : "transparent", color: sel ? "var(--accent-blue)" : "var(--text-secondary)", fontSize: "12px", fontWeight: "600", cursor: "pointer", transition: "all 0.15s" }}>
                    {sel ? "✓ " : ""}{st}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="card">
            <h3 style={{ fontSize: "15px", fontWeight: "700", marginBottom: "16px" }}>Contract Value Range</h3>
            <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "16px" }}>Tenders outside this range will score lower in AI matching.</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Min Value (₦)</label>
                <input type="number" value={form.min_contract_value} onChange={e => update("min_contract_value", e.target.value)} placeholder="e.g. 5000000" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Max Value (₦)</label>
                <input type="number" value={form.max_contract_value} onChange={e => update("max_contract_value", e.target.value)} placeholder="e.g. 500000000" style={inputStyle} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── COMPLIANCE TAB ──────────────────────────────────────── */}
      {activeTab === "compliance" && (
        <div className="card">
          <h3 style={{ fontSize: "15px", fontWeight: "700", marginBottom: "4px" }}>Compliance Certificates</h3>
          <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "20px" }}>
            Track which certificates your company holds. These are checked in AI bid compliance scoring.
            Manage deadlines in the <a href="/#/regulatory-calendar" style={{ color: "var(--accent-blue)", textDecoration: "none" }}>Regulatory Calendar →</a>
          </p>

          {[
            { label: "CAC Certificate",       desc: "Corporate Affairs Commission registration" },
            { label: "Tax Clearance (TCC)",    desc: "Federal Inland Revenue Service — last 3 years" },
            { label: "PENCOM Compliance",      desc: "Pension fund contributions certificate" },
            { label: "NSITF Certificate",      desc: "Employee compensation fund" },
            { label: "ITF Compliance",         desc: "Industrial Training Fund levy" },
            { label: "NAFDAC Registration",    desc: "If applicable to your products/services" },
            { label: "ISO 9001 Certification", desc: "Quality management system" },
            { label: "NUPRC Licence",          desc: "If operating in oil & gas upstream sector" },
          ].map(({ label, desc }) => {
            const doc = documents.find(d => d.document_type === label);
            const uploading = uploadingType === label;
            return (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: "1px solid var(--border)", flexWrap: "wrap", gap: "10px" }}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: "600", marginBottom: "2px" }}>{label}</div>
                  <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>{desc}</div>
                  {doc?.is_verified === false && doc?.notes && (
                    <div style={{ fontSize: "11px", color: "#ff4d6d", marginTop: "4px" }}>Rejected: {doc.notes}</div>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                  {doc ? (
                    <>
                      <span style={{
                        display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", padding: "4px 10px", borderRadius: "100px", fontWeight: "700",
                        background: doc.is_verified ? "rgba(0,212,161,0.1)" : doc.notes ? "rgba(255,77,109,0.1)" : "rgba(255,140,66,0.1)",
                        color: doc.is_verified ? "var(--accent-green)" : doc.notes ? "#ff4d6d" : "var(--accent-orange)",
                      }}>
                        {doc.is_verified ? <CheckCircle size={11} /> : doc.notes ? <XCircle size={11} /> : <Clock size={11} />}
                        {doc.is_verified ? "Verified" : doc.notes ? "Rejected" : "Pending Review"}
                      </span>
                      {!doc.is_verified && (
                        <button onClick={() => handleDeleteDocument(doc.id)} style={{ fontSize: "11px", padding: "4px 10px", background: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: "6px", color: "var(--text-muted)", cursor: "pointer" }}>
                          Remove
                        </button>
                      )}
                    </>
                  ) : (
                    <label style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "12px", padding: "6px 14px", background: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: "8px", cursor: uploading ? "default" : "pointer", color: "var(--accent-blue)", fontWeight: "600" }}>
                      {uploading ? <><RefreshCw size={13} style={{ animation: "spin 0.8s linear infinite" }} /> Uploading...</> : <><Upload size={13} /> Upload</>}
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" disabled={uploading} style={{ display: "none" }}
                        onChange={e => { handleUploadDocument(label, e.target.files[0]); e.target.value = ""; }} />
                    </label>
                  )}
                </div>
              </div>
            );
          })}

          <div style={{ marginTop: "20px", padding: "14px 16px", background: "rgba(79,142,247,0.06)", border: "1px solid rgba(79,142,247,0.15)", borderRadius: "10px", fontSize: "13px", color: "var(--text-secondary)", lineHeight: "1.6" }}>
            <strong style={{ color: "var(--text-primary)" }}>Pro tip:</strong> Use the Regulatory Calendar to set reminders before each certificate expires. ProTrackNG will alert you 30, 14, and 7 days in advance so you are never disqualified from a bid due to expired documents.
          </div>
        </div>
      )}

      {/* Bottom save button */}
      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={saving}
        style={{ marginTop: "24px", padding: "12px 32px" }}
      >
        {saving ? (
          <><RefreshCw size={14} style={{ animation: "spin 0.8s linear infinite" }} /> Saving...</>
        ) : saved ? (
          <><CheckCircle size={14} /> Saved!</>
        ) : (
          <><Save size={14} /> Save Changes</>
        )}
      </button>
      <style>{"@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}
